import React, { ReactElement, useContext, useEffect, useState } from "react";
import { animated, useSpring } from "react-spring";
import TablePagination from "@material-ui/core/TablePagination";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import CustomFooterGraphic from "../../Requests/CustomFooterGraphic";
import CustomProgress from "../../../components/Alert/CustomProgress";
import ProductApi from "../../../api/services/ProductApi";
import { getRowsPerPage, setRowsPerPage } from "../../../helper/UserSettingsHelper";
import useEnv from "../../../hooks/env";
import { fadeIn } from "../../../styles/springs";
import PagedResponse from "../../../api/models/PagedResponse";
import CompetitorProduct from "../../../api/models/product/CompetitorProduct";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";

interface CompetitorProductOverviewTableProps {
  competitorProductPagedRes: PagedResponse<CompetitorProduct> | undefined;
  handleEdit: (row: CustomTableRowData) => void;
  setCompetitorProductPagedRes: React.Dispatch<React.SetStateAction<PagedResponse<CompetitorProduct> | undefined>>;
}

const CompetitorProductOverviewTable = (props: CompetitorProductOverviewTableProps): ReactElement => {
  const { competitorProductPagedRes, setCompetitorProductPagedRes, handleEdit } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(getRowsPerPage());
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const springProps = useSpring(fadeIn);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPageIndex(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const rowsPerPage = parseInt(event.target.value, 10);
    setPageSize(rowsPerPage);
    setRowsPerPage(rowsPerPage);
    setPageIndex(0);
  };

  const generateRows = (productsArr: CompetitorProduct[] | undefined): void => {
    const productRows =
      productsArr && productsArr.map
        ? productsArr.map((product) => {
            return {
              id: product.id,
              product: product.name,
              company: product.company?.name,
              adhesive: product.adhesive,
              backing: product.backing,
              creationDate: product.creationDate,
            };
          })
        : [];

    setRows(productRows as never);
  };

  useEffect(() => {
    generateRows(competitorProductPagedRes?.data);
    setPageIndex(competitorProductPagedRes?.pageIndex || 0);
    setPageSize(competitorProductPagedRes?.pageSize || getRowsPerPage());
    setIsLoading(false);
  }, [competitorProductPagedRes]);

  useEffect(() => {
    setIsLoading(true);
    productApi
      .getCompetitorProduct("", undefined, pageIndex, pageSize)
      .then((res) => {
        setCompetitorProductPagedRes(res.data);
      })
      .catch(handleHttpError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [pageSize, pageIndex]);

  return isLoading ? (
    <CustomProgress />
  ) : (
    <>
      <animated.div style={springProps}>
        <CustomTable
          boldIndex={1}
          columns={["ID", "Product", "Company", "Adhesive", "Backing", "Creation Date"]}
          columnMaxWidths={["4rem", "15vw", "10vw", "10vw", "15vw", "4rem"]}
          actionColumnIndexes={[1]}
          formatDates
          rows={rows}
          handleEdit={handleEdit}
        />
        <TablePagination
          component="div"
          count={competitorProductPagedRes?.totalData || 0}
          page={pageIndex}
          onChangePage={handleChangePage}
          rowsPerPage={pageSize}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
        <CustomFooterGraphic />
      </animated.div>
    </>
  );
};

export default CompetitorProductOverviewTable;
